import React, { useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { longDateString } from "../../utils";
import "../../starek.css";

function OperationsListingView({
    items,
    selectedRows,
    onSelectedRowsChange,
    filterText,
    noDataComponent,
    onRowClicked,
}) {
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const isOperationSelected = (operationId) => {
        return selectedRows.some((row) => row.id === operationId);
    };

    const handleCheckboxChange = (operation) => {
        let newSelectedRows;
        if (isOperationSelected(operation.id)) {
            newSelectedRows = selectedRows.filter(
                (row) => row.id !== operation.id,
            );
        } else {
            newSelectedRows = [...selectedRows, operation];
        }
        if (onSelectedRowsChange) {
            onSelectedRowsChange({ selectedRows: newSelectedRows });
        }
    };

    const filteredItems = useMemo(() => {
        const q = filterText?.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q),
        );
    }, [items, filterText]);

    const currentPageItems = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        return filteredItems.slice(start, start + rowsPerPage);
    }, [filteredItems, currentPage, rowsPerPage]);

    const columns = [
        {
            name: (
                <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    onChange={(e) => {
                        if (e.target.checked) {
                            const newRows = [
                                ...selectedRows,
                                ...currentPageItems.filter(
                                    (r) => !isOperationSelected(r.id),
                                ),
                            ];
                            if (onSelectedRowsChange) {
                                onSelectedRowsChange({ selectedRows: newRows });
                            }
                        } else {
                            const pageIds = new Set(
                                currentPageItems.map((r) => r.id),
                            );
                            if (onSelectedRowsChange) {
                                onSelectedRowsChange({
                                    selectedRows: selectedRows.filter(
                                        (r) => !pageIds.has(r.id),
                                    ),
                                });
                            }
                        }
                    }}
                    checked={
                        currentPageItems.length > 0 &&
                        currentPageItems.every((row) =>
                            isOperationSelected(row.id),
                        )
                    }
                    ref={(el) => {
                        if (!el) return;
                        const some = currentPageItems.some((r) =>
                            isOperationSelected(r.id),
                        );
                        const all =
                            currentPageItems.length > 0 &&
                            currentPageItems.every((r) =>
                                isOperationSelected(r.id),
                            );
                        el.indeterminate = some && !all;
                    }}
                />
            ),
            width: "60px",
            center: true,
            sortable: false,
            cell: (row) => (
                <input
                    type="checkbox"
                    checked={isOperationSelected(row.id)}
                    onChange={() => handleCheckboxChange(row)}
                    aria-label={`Select ${row.name}`}
                />
            ),
            ignoreRowClick: true,
        },
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: "Description",
            selector: (row) => row.description,
            sortable: true,
            grow: 2,
        },
        {
            name: "Category",
            selector: (row) => row.is_productive,
            sortable: true,
            width: "150px",
            cell: (row) => (
                <span
                    className={`badge bg-${row.is_productive ? "success" : "danger"}`}
                >
                    {row.is_productive ? "Productive" : "Non-productive"}
                </span>
            ),
        },
        {
            name: "Activity",
            selector: (row) => row.updated_at,
            sortable: true,
            width: "250px",
            cell: (row) => (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip>
                            <div>
                                Created by {row.created_by?.name || "Unknown"}{" "}
                                at {longDateString(row.created_at)}
                            </div>
                            <div>
                                Modified by {row.updated_by?.name || "Unknown"}{" "}
                                at {longDateString(row.updated_at)}
                            </div>
                        </Tooltip>
                    }
                >
                    <div style={{ fontSize: "0.85rem" }}>
                        {row.updated_by?.name || "Unknown"} at{" "}
                        {longDateString(row.updated_at)}
                    </div>
                </OverlayTrigger>
            ),
        },
        {
            name: "Status",
            width: "90px",
            cell: (r) => (
                <span
                    className={`badge bg-${r.is_active ? "success" : "secondary"}`}
                >
                    {r.is_active ? "Active" : "Inactive"}
                </span>
            ),
        },
    ];

    const conditionalRowStyles = [
        {
            when: (r) => !r.is_active,
            style: { opacity: 0.5, backgroundColor: "#f8f9fa" },
        },
    ];

    return (
        <DataTable
            columns={columns}
            data={filteredItems}
            highlightOnHover
            pointerOnHover
            onRowClicked={(row) => onRowClicked?.(row)}
            striped
            responsive
            pagination
            dense
            onChangePage={(page) => setCurrentPage(page)}
            onChangeRowsPerPage={(newPerPage, page) => {
                setRowsPerPage(newPerPage);
                setCurrentPage(page);
            }}
            noDataComponent={noDataComponent}
            conditionalRowStyles={conditionalRowStyles}
        />
    );
}

export default OperationsListingView;
